import { useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

import PageHeader from '../../components/PageHeader';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtShortDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return `${MONTHS[date.getMonth()]}-${String(date.getDate()).padStart(2, '0')} '${String(date.getFullYear()).slice(2)}`;
};

// GraphQL Query
const GET_USER_ACTIVITY_STATS = gql`
  query GetUserActivityStats($timeframe: String) {
    userActivityStats(timeframe: $timeframe) {
      userId
      displayName
      totalValidations
      orgCreatedCount
      dealCreatedCount
      originationCallCount
    }
  }
`;

const USER_ACTIVITY_DRILLDOWN_QUERY = gql`
  query UserActivityDrilldown($timeframe: String, $userId: Int!, $metric: String!) {
    userActivityDrilldown(timeframe: $timeframe, userId: $userId, metric: $metric) {
      companyId
      companyName
      domain
      callabilityScore
      fte
      ebitda
      validationDates
      meetings {
        meetingId
        meetingDate
        attendees
      }
    }
  }
`;

// Timeframe options
const TIMEFRAME_OPTIONS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'ytd', label: 'YTD' },
  { value: 'ltm', label: 'LTM' },
];

// Types
interface UserActivityData {
  userId: number;
  displayName: string;
  totalValidations: number;
  orgCreatedCount: number;
  dealCreatedCount: number;
  originationCallCount: number;
}

interface UserActivityResponse {
  userActivityStats: UserActivityData[];
}

// Main component
const UserActivityPage = ({ embedded = false }: { embedded?: boolean }) => {
  const theme = useTheme();
  const [timeframe, setTimeframe] = useState('last_7_days');

  const { loading, error, data, refetch } = useQuery<UserActivityResponse>(GET_USER_ACTIVITY_STATS, {
    variables: { timeframe },
    fetchPolicy: 'cache-and-network',
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleTimeframeChange = (event: any) => {
    setTimeframe(event.target.value);
  };

  const [drillDown, setDrillDown] = useState<{ userId: number; metric: string; label: string } | null>(null);
  const [fetchDrilldown, { loading: drillLoading, data: drillData }] = useLazyQuery(USER_ACTIVITY_DRILLDOWN_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleBarClick = (_event: any, d: any) => {
    const user = users[d.dataIndex];
    if (!user) return;
    const seriesMap: Record<string, { metric: string; label: string }> = {
      total: { metric: 'validations', label: `${user.displayName} — Validations` },
      orgCreated: { metric: 'org_created', label: `${user.displayName} — Org Created` },
      dealCreated: { metric: 'deal_created', label: `${user.displayName} — Deal Created` },
      originationCalls: { metric: 'origination_calls', label: `${user.displayName} — Origination Calls` },
    };
    const info = seriesMap[d.seriesId];
    if (!info) return;
    setDrillDown({ userId: user.userId, metric: info.metric, label: info.label });
    fetchDrilldown({ variables: { timeframe, userId: user.userId, metric: info.metric } });
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography variant="h6" color="error">
          Error loading user activity statistics
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {error.message}
        </Typography>
        <Button variant="outlined" color="primary" onClick={() => refetch()} sx={{ mt: 2 }} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  const users = data?.userActivityStats || [];
  const selectedTimeframeLabel = TIMEFRAME_OPTIONS.find(opt => opt.value === timeframe)?.label || 'Last 7 Days';

  // Prepare chart data
  const userNames = users.map(user => user.displayName);
  const totalValidationsData = users.map(user => user.totalValidations);
  const orgCreatedData = users.map(user => user.orgCreatedCount);
  const dealCreatedData = users.map(user => user.dealCreatedCount);
  const originationCallData = users.map(user => user.originationCallCount);

  return (
    <Box>
      {!embedded && (
        <PageHeader
          title="User Activity Analytics"
          subtitle="Monitor user validation activity and performance metrics"
          variant="default"
        />
      )}

      <Box sx={embedded ? {} : { p: 3 }}>
        {error && data && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: alpha('#FF564F', 0.1),
              borderLeft: '4px solid #FF564F',
            }}
          >
            <Typography variant="body2" color="error">
              Unable to fetch latest data. Showing cached results.
            </Typography>
          </Paper>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Active Users: {users.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Validation activity for {selectedTimeframeLabel.toLowerCase()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Timeframe</InputLabel>
              <Select value={timeframe} label="Timeframe" onChange={handleTimeframeChange}>
                {TIMEFRAME_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Refresh
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {users.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh">
            <Typography variant="h6" color="text.secondary">
              No user activity found
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              User validation activity will appear here once users complete validations
            </Typography>
          </Box>
        ) : (
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Validation Activity Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Comparison of total validations vs actions taken (Org Created / Deal Created)
              </Typography>

              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <BarChart
                  height={Math.max(400, users.length * 50)}
                  series={[
                    {
                      data: totalValidationsData,
                      label: 'Total Validations',
                      id: 'total',
                      color: theme.palette.info.main,
                    },
                    {
                      data: orgCreatedData,
                      label: 'Org Created',
                      id: 'orgCreated',
                      color: theme.palette.success.main,
                    },
                    {
                      data: dealCreatedData,
                      label: 'Deal Created',
                      id: 'dealCreated',
                      color: theme.palette.primary.main,
                    },
                    {
                      data: originationCallData,
                      label: 'Origination Calls',
                      id: 'originationCalls',
                      color: theme.palette.warning.main,
                    },
                  ]}
                  yAxis={[
                    {
                      scaleType: 'band',
                      data: userNames,
                    },
                  ]}
                  layout="horizontal"
                  margin={{ left: 150, right: 20, top: 20, bottom: 60 }}
                  onItemClick={handleBarClick}
                  slotProps={{
                    legend: {
                      direction: 'row',
                      position: { vertical: 'bottom', horizontal: 'middle' },
                      padding: 0,
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} mb={2}>
                Detailed Statistics
              </Typography>

              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${theme.palette.divider}` }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        User
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Total Validations
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Org Created
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Deal Created
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Origination Calls
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr
                        key={user.userId}
                        style={{
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <td style={{ padding: '12px 8px', fontWeight: 500 }}>{user.displayName}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>
                          {user.totalValidations}
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            color: theme.palette.success.main,
                            fontWeight: 500,
                          }}
                        >
                          {user.orgCreatedCount}
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                          }}
                        >
                          {user.dealCreatedCount}
                        </td>
                        <td
                          style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            color: theme.palette.warning.main,
                            fontWeight: 500,
                          }}
                        >
                          {user.originationCallCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {drillDown && (
        <Dialog
          open
          onClose={() => setDrillDown(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {drillDown.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {drillData?.userActivityDrilldown?.length ?? 0} companies
              </Typography>
            </Box>
            <IconButton onClick={() => setDrillDown(null)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {drillLoading && (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={36} sx={{ color: '#FF564F' }} />
              </Box>
            )}
            {!drillLoading && (!drillData?.userActivityDrilldown || drillData.userActivityDrilldown.length === 0) && (
              <Typography color="text.secondary" py={4} textAlign="center">
                No companies found
              </Typography>
            )}
            {!drillLoading && drillData?.userActivityDrilldown?.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Company', 'Domain', 'Callability', 'FTE', 'EBITDA', 'Validated', 'Call Date', 'Team'].map(
                        (h, i) => (
                          <th
                            key={h}
                            style={{
                              textAlign: i >= 2 && i <= 4 ? 'right' : 'left',
                              padding: '10px 8px',
                              fontWeight: 600,
                              color: theme.palette.text.secondary,
                              borderBottom: `2px solid ${theme.palette.divider}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {drillData.userActivityDrilldown.map((company: any) =>
                      (company.meetings?.length ? company.meetings : [{}]).map((meeting: any, mIdx: number) => (
                        <tr
                          key={`${company.companyId}-${meeting.meetingId || mIdx}`}
                          style={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                        >
                          <td style={{ padding: '10px 8px', fontWeight: mIdx === 0 ? 600 : 400 }}>
                            {mIdx === 0 ? (
                              <a
                                href={`/evaluation/dfm?companyId=${company.companyId}`}
                                style={{
                                  color: '#3F5D66',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                }}
                                onMouseEnter={e => {
                                  (e.target as HTMLElement).style.textDecoration = 'underline';
                                }}
                                onMouseLeave={e => {
                                  (e.target as HTMLElement).style.textDecoration = 'none';
                                }}
                              >
                                {company.companyName}
                              </a>
                            ) : (
                              ''
                            )}
                          </td>
                          <td style={{ padding: '10px 8px', color: theme.palette.text.secondary }}>
                            {mIdx === 0 && company.domain ? (
                              <a
                                href={`https://${company.domain}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: theme.palette.text.secondary,
                                  textDecoration: 'none',
                                }}
                                onMouseEnter={e => {
                                  (e.target as HTMLElement).style.textDecoration = 'underline';
                                }}
                                onMouseLeave={e => {
                                  (e.target as HTMLElement).style.textDecoration = 'none';
                                }}
                              >
                                {company.domain}
                              </a>
                            ) : (
                              ''
                            )}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                            {mIdx === 0
                              ? company.callabilityScore !== null && company.callabilityScore !== undefined
                                ? company.callabilityScore.toFixed(2)
                                : '—'
                              : ''}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                            {mIdx === 0 ? (company.fte != null ? company.fte.toLocaleString() : '—') : ''}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                            {mIdx === 0
                              ? company.ebitda != null
                                ? `${(company.ebitda / 1000).toFixed(0)}k`
                                : '—'
                              : ''}
                          </td>
                          <td style={{ padding: '10px 8px' }}>
                            {mIdx === 0
                              ? (() => {
                                  const dates = company.validationDates || [];
                                  if (dates.length === 0) return '—';
                                  const first = fmtShortDate(dates[0]);
                                  if (dates.length === 1) return first;
                                  return (
                                    <Tooltip
                                      title={
                                        <Box>
                                          <Typography variant="caption" fontWeight={600}>
                                            Re-validations:
                                          </Typography>
                                          {dates.slice(1).map((d: string, i: number) => (
                                            <Typography key={i} variant="caption" display="block">
                                              {fmtShortDate(d)}
                                            </Typography>
                                          ))}
                                        </Box>
                                      }
                                      arrow
                                    >
                                      <span style={{ borderBottom: '1px dotted #A6A6A7', cursor: 'help' }}>
                                        {first} (+{dates.length - 1})
                                      </span>
                                    </Tooltip>
                                  );
                                })()
                              : ''}
                          </td>
                          <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                            {fmtShortDate(meeting.meetingDate) || (mIdx === 0 ? '—' : '')}
                          </td>
                          <td style={{ padding: '10px 8px' }}>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {(meeting.attendees || []).map((name: string) => (
                                <Chip key={name} label={name} size="small" variant="outlined" sx={{ fontSize: 12 }} />
                              ))}
                            </Box>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default UserActivityPage;
